import { Injectable } from "@angular/core";

import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import { AIUsage } from "@admin/components/ai-usage/models/ai-usage.model";
import { TokenResponse } from "@core/services/auth.service";
import { RegisterInvitedDto, RegisterLocalDto } from "@login/models/register.model";
import { AuthProvider } from "@shared/models/types.model";
import { User } from "@shared/models/user.model";
import { BaseHttpService } from "@shared/services/base-http.service";
@Injectable({
  providedIn: "root"
})
export class AuthHttpService extends BaseHttpService {
  protected override baseEndpoint = "auth";

  loginWithProvider(provider: AuthProvider, organizationId?: string | null): Observable<string> {
    return this.get<{ authorizationUrl: string }>({
      endpoint: `${provider}/login`,
      options: organizationId ? { params: { organizationId } } : {},
      showToast: false
    }).pipe(map((response) => response.authorizationUrl));
  }

  loginWithProviderForUpgrade(provider: AuthProvider): Observable<string> {
    return this.get<{ authorizationUrl: string }>({
      endpoint: `${provider}/login`,
      options: { params: { upgrade: true } },
      showToast: false
    }).pipe(map((response) => response.authorizationUrl));
  }

  checkEmail(email: string): Observable<{ exists: boolean }> {
    return this.post<{ exists: boolean }>({
      endpoint: "check-email",
      body: { email },
      showToast: false
    });
  }

  requestPasswordReset(email: string): Observable<{ success: boolean; message: string }> {
    return this.post<{ success: boolean; message: string }>({
      endpoint: "password-reset/request",
      body: { email },
      showToast: false
    });
  }

  verifyResetToken(token: string, email: string): Observable<{ success: boolean; message: string }> {
    return this.get<{ success: boolean; message: string }>({
      endpoint: "password-reset/verify",
      options: { params: { token, email } },
      showToast: false
    });
  }

  resetPassword(token: string, email: string, password: string): Observable<{ success: boolean; message: string }> {
    return this.post<{ success: boolean; message: string }>({
      endpoint: "password-reset/reset",
      body: { token, email, password },
      showToast: false
    });
  }

  verifyEmail(token: string, email: string): Observable<{ success: boolean; message: string; alreadyConfirmed: boolean }> {
    return this.get<{ success: boolean; message: string; alreadyConfirmed: boolean }>({
      endpoint: "email-verification/verify",
      options: { params: { token, email } },
      showToast: false
    });
  }

  resendEmailVerification(email: string): Observable<{ success: boolean; message: string }> {
    return this.post<{ success: boolean; message: string }>({
      endpoint: "email-verification/resend",
      body: { email },
      showToast: false
    });
  }

  loginWithCredentials(username: string, password: string): Observable<TokenResponse> {
    return this.post<TokenResponse>({
      endpoint: "login",
      body: { username, password },
      showToast: false
    });
  }

  getMe(): Observable<User> {
    return this.get<User>({ endpoint: "me", type: User, showToast: false });
  }

  getUsage(): Observable<AIUsage> {
    return this.get<AIUsage>({ endpoint: "me/usage", showToast: false });
  }

  logout(): Observable<{ success: boolean }> {
    return this.post<{ success: boolean }>({ endpoint: "logout", showToast: false });
  }

  deleteAccount(): Observable<void> {
    return this.delete<void>({ endpoint: "account", showToast: false });
  }

  updateProfile(firstName?: string, lastName?: string): Observable<User> {
    return this.patch<User>({
      endpoint: "me/profile",
      body: { firstName, lastName },
      type: User
    });
  }

  updatePreferences(preferences: any): Observable<User> {
    return this.patch<User>({
      endpoint: "me/preferences",
      body: { preferences },
      type: User
    });
  }

  uploadAvatar(file: File): Observable<User> {
    const formData = new FormData();
    formData.append("avatar", file);
    return this.post<User>({
      endpoint: "me/avatar",
      body: formData,
      type: User
    });
  }

  deleteAvatar(): Observable<User> {
    return this.delete<User>({
      endpoint: "me/avatar",
      type: User
    });
  }

  refresh(): Observable<TokenResponse> {
    return this.post<TokenResponse>({
      endpoint: "refresh",
      showToast: false
    });
  }

  registerInvitedUser(data: RegisterInvitedDto): Observable<void> {
    // Only send username if it's not empty
    const payload = {
      email: data.email,
      password: data.password,
      organizationId: data.organizationId,
      ...(data.username ? { username: data.username } : {})
    };

    return this.post<void>({
      endpoint: "register-invited",
      body: payload,
      showToast: false
    });
  }

  registerLocal(data: RegisterLocalDto): Observable<TokenResponse> {
    return this.post<TokenResponse>({
      endpoint: "register",
      body: data,
      showToast: false
    });
  }

  createGuest(): Observable<TokenResponse> {
    return this.post<TokenResponse>({ endpoint: "guest", showToast: false });
  }

  upgradeFromGuest(payload: { email: string; password: string; username?: string }): Observable<TokenResponse> {
    return this.post<TokenResponse>({ endpoint: "upgrade", body: payload, showToast: false });
  }


  impersonate(userId: string): Observable<TokenResponse> {
    return this.post<TokenResponse>({
      endpoint: `impersonate/${userId}`,
      showToast: false
    });
  }

  revertImpersonation(): Observable<TokenResponse> {
    return this.post<TokenResponse>({
      endpoint: "revert-impersonation",
      showToast: false
    });
  }
}
